import { GoogleTagManager } from '@next/third-parties/google';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import type { AnalyticsEvent, AnalyticsEventEnriched, UTM } from '@/core/lib/analytics/analytics.context';
import { AnalyticsContext } from '@/core/lib/analytics/analytics.context';
import { useEnvContext } from '@/core/lib/env/env.context';
import api from '@/core/lib/fetch/api';
import logger from '@/core/lib/log';
import Store from '@/core/lib/new-architecture/store';
import { AbstractRoute } from '@/core/lib/router/route';

const AnalyticsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { GTM_ID, GTM_AUTH, GTM_PREVIEW, env, BASE_URL } = useEnvContext();
  const { data: currentUser } = Store.current.useCurrent();

  const [utm, setUtm] = useState<UTM | null>(null);

  const {
    common: { utm_source, utm_medium, utm_campaign },
  } = AbstractRoute.useCommonParams();

  useEffect(() => {
    if (utm_source || utm_medium || utm_campaign) {
      setUtm({
        utmSource: utm_source ?? null,
        utmMedium: utm_medium ?? null,
        utmCampaign: utm_campaign ?? null,
      });
    }
  }, [utm_source, utm_medium, utm_campaign]);

  const getUtmOrigin = useCallback(() => {
    if (!utm) return null;

    const origin = [];

    if (utm.utmSource) {
      origin.push(`s=${utm.utmSource}`);
    }

    if (utm.utmMedium) {
      origin.push(`m=${utm.utmMedium}`);
    }

    if (utm.utmCampaign) {
      origin.push(`c=${utm.utmCampaign}`);
    }

    return origin.join('&').substring(0, 255);
  }, [utm]);

  const send = useCallback(async (event: AnalyticsEvent) => {
    const fullEvent = {
      ...event,
      platform: 'web',
      version: 'web',
      event_id: uuidv4(),
    } satisfies AnalyticsEventEnriched;

    if (env === 'production') {
      try {
        await api.post({
          url: `${BASE_URL}/api/analytics/meta`,
          data: { ...fullEvent, client_user_agent: navigator.userAgent, user_uuid: currentUser?.uuid },
        });

        window.dataLayer?.push({ ...fullEvent });
        return;
      } catch (error) {
        console.error(error);
        return;
      }
    }
    logger.info({ fullEvent });
  }, []);

  const value = useMemo(() => ({ send, utm, getUtmOrigin }), [utm]);

  return (
    <AnalyticsContext.Provider value={value}>
      <GoogleTagManager gtmId={GTM_ID} auth={GTM_AUTH} preview={GTM_PREVIEW} />
      {children}
    </AnalyticsContext.Provider>
  );
};

export default AnalyticsProvider;
